import styled from "styled-components";
import { Link, NavLink } from "react-router-dom";

/* ─── Root wrapper ───────────────────────────────────────────── */

export const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f4f6f8;
`;

/* ─── Sidebar ────────────────────────────────────────────────── */

export const Sidebar = styled.aside`
  width: 240px;
  flex-shrink: 0;
  background-color: #ffffff;
  border-right: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 100;
`;

export const SidebarLogo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 20px;
  border-bottom: 1px solid #f0f0f0;
  text-decoration: none;

  img {
    height: 24px;
    width: auto;
  }
`;

export const SidebarBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #1fbc70;
  background-color: #f0fdf7;
  border: 1px solid #c6f0dc;
  border-radius: 4px;
  padding: 2px 6px;
  letter-spacing: 0.04em;
`;

export const NavSection = styled.div`
  padding: 16px 12px 8px;
`;

export const NavSectionLabel = styled.p`
  font-size: 10px;
  font-weight: 700;
  color: #bbb;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0 8px;
  margin-bottom: 4px;
`;

export const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  text-decoration: none;
  transition: background-color 0.15s, color 0.15s;
  margin-bottom: 2px;

  &:hover {
    background-color: #f5f5f5;
    color: #111;
  }

  &.active {
    background-color: #f0fdf7;
    color: #1fbc70;
    font-weight: 600;
  }
`;

export const NavIcon = styled.span`
  font-size: 16px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
`;

export const NavParent = styled.button<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ isOpen }) => (isOpen ? "#1fbc70" : "#555")};
  background: ${({ isOpen }) => (isOpen ? "#f0fdf7" : "none")};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s, color 0.15s;
  margin-bottom: 2px;

  &:hover {
    background-color: ${({ isOpen }) => (isOpen ? "#f0fdf7" : "#f5f5f5")};
    color: ${({ isOpen }) => (isOpen ? "#1fbc70" : "#111")};
  }
`;

export const NavChevron = styled.span<{ isOpen: boolean }>`
  margin-left: auto;
  font-size: 11px;
  color: #bbb;
  transition: transform 0.2s;
  transform: ${({ isOpen }) => (isOpen ? "rotate(90deg)" : "rotate(0deg)")};
`;

export const SubNavWrap = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? "200px" : "0")};
  transition: max-height 0.2s ease;
  margin-bottom: ${({ isOpen }) => (isOpen ? "2px" : "0")};
`;

export const SubNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px 8px 42px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #777;
  text-decoration: none;
  transition: background-color 0.15s, color 0.15s;
  margin-bottom: 2px;

  &:hover {
    background-color: #f5f5f5;
    color: #111;
  }

  &.active {
    background-color: #f0fdf7;
    color: #1fbc70;
    font-weight: 600;
  }
`;

export const SidebarBottom = styled.div`
  margin-top: auto;
  padding: 16px 12px;
  border-top: 1px solid #f0f0f0;
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background-color: #fff0f2;
    color: #fc324a;
  }
`;

/* ─── Main area ──────────────────────────────────────────────── */

export const Main = styled.div`
  flex: 1;
  margin-left: 240px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  height: 60px;
  background-color: #ffffff;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
`;

export const PageTitle = styled.h1`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.01em;
`;

export const AdminInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const AdminAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: #f0fdf7;
  border: 1.5px solid #c6f0dc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
`;

export const AdminName = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
`;

export const AdminUniversity = styled.span`
  display: block;
  font-size: 11px;
  color: #aaa;
  line-height: 1.3;
`;

export const Content = styled.main`
  flex: 1;
  padding: 28px;
`;
